import { PlusIcon, SearchIcon } from "@heroicons/react/outline";
import { useState } from "react";

import McpServerDialog from "./McpServerDialog";
import McpServerCard from "./McpServerCard";

import { ConfirmationModalVariant } from "@/components/ConfirmationModal";
import { Button } from "@/components/ui/button";
import { Mcp } from "@/components/ui/icons";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/auth-context";
import { useConfirm } from "@/context/confirm-context";
import { useUser } from "@/context/user-context";
import { useDebounce } from "@/hooks";
import { McpServer, useDeleteMcpServer, useMcpServers } from "@/queries/mcp-servers";
import { displayErrorToast } from "@/utils/toast";

const McpServers = () => {
  const { confirm } = useConfirm();
  const workspaceId = useUser()?.activeWorkspaceId ?? 0;
  const userToken = useAuth()?.userToken ?? "";
  const [search, setSearch] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingServer, setEditingServer] = useState<McpServer | null>(null);
  const debouncedSearch = useDebounce(search, 300);
  const servers = useMcpServers({ workspaceId, userToken, search: debouncedSearch });
  const deleteServer = useDeleteMcpServer(workspaceId, userToken);

  const openCreateDialog = () => {
    setEditingServer(null);
    setIsDialogOpen(true);
  };

  const openEditDialog = (server: McpServer) => {
    setEditingServer(server);
    setIsDialogOpen(true);
  };

  const handleDelete = async (server: McpServer) => {
    const confirmed = await confirm({
      title: `Delete MCP server: "${server.name}"`,
      message: "This MCP server will be permanently deleted. This action cannot be undone.",
      confirmText: "Delete MCP Server",
      cancelText: "Cancel",
      headerVariant: ConfirmationModalVariant.destructive,
      saveButtonVariant: "destructive",
      exitButtonVariant: "outline",
      requireConfirmation: true,
      confirmationText: server.name,
      confirmationPlaceholder: server.name,
    });

    if (!confirmed) {
      return;
    }

    try {
      await deleteServer.mutateAsync(server.id);
    } catch (error) {
      displayErrorToast(error);
    }
  };

  const hasActiveSearch = Boolean(debouncedSearch.trim());
  const serverList = servers.data ?? [];

  let serversContent = (
    <div className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2">
      {serverList.map(server => (
        <McpServerCard
          key={server.id}
          server={server}
          onEdit={openEditDialog}
          onDelete={serverToDelete => void handleDelete(serverToDelete)}
          isDeleting={deleteServer.isPending}
        />
      ))}
    </div>
  );

  if (servers.isLoading) {
    serversContent = (
      <div className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  } else if (serverList.length === 0 && !hasActiveSearch) {
    serversContent = (
      <div className="mt-12 flex flex-1 flex-col items-center justify-center px-8 text-center">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-gray-100 text-gray-700">
          <Mcp className="h-8 w-8" />
        </div>
        <h3 className="mb-2 text-lg font-medium text-gray-900">No MCP servers yet</h3>
        <p className="mb-6 text-gray-500">
          Create a connection to make its tools reusable in prompts.
        </p>
        <Button type="button" onClick={openCreateDialog} size="lg">
          <PlusIcon className="mr-2 h-4 w-4" />
          Create New MCP Server
        </Button>
      </div>
    );
  } else if (serverList.length === 0) {
    serversContent = (
      <div className="mt-12 flex flex-1 flex-col items-center justify-center px-8 text-center">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-gray-100 text-gray-500">
          <Mcp className="h-8 w-8" />
        </div>
        <h3 className="mb-2 text-lg font-medium text-gray-900">No MCP servers found</h3>
        <p className="text-gray-500">
          Try adjusting your search to find what you&apos;re looking for.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="my-2 flex items-center gap-3 px-1">
        <div className="relative min-w-0 flex-1">
          <SearchIcon className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
          <Input
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Search MCP servers..."
            className="box-border h-10 w-full border-0 bg-gray-100 pl-10 pr-4 text-sm shadow-none focus:border-0 focus:ring-0"
          />
        </div>
        <Button type="button" onClick={openCreateDialog}>
          <PlusIcon className="mr-2 h-4 w-4" />
          New MCP Server
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto py-2">{serversContent}</div>

      <McpServerDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        server={editingServer}
        workspaceId={workspaceId}
        userToken={userToken}
      />
    </div>
  );
};

export default McpServers;
